import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useApi from '../api';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import { Plus, TriangleAlert } from 'lucide-react';
import {
    ACQUISITION_OPTIONS,
    Service,
    ServiceStatusBadge,
    describePublication,
    formatRelativeTime,
    publicationLabel,
} from './servicesShared';
import ServiceSdkKeysTab from './ServiceSdkKeysTab';
import { EventsTab } from './EventsPage';
import JobsList from '../jobs/JobsList';
import NeedsYou from '../jobs/NeedsYou';

const TABS = ['services', 'jobs', 'needs-you', 'events', 'sdk'] as const;
type Tab = (typeof TABS)[number];

const ServicesPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { get } = useApi();

    const tabParam = searchParams.get('tab');
    const activeTab: Tab = TABS.includes(tabParam as Tab) ? (tabParam as Tab) : 'services';

    const [loading, setLoading] = useState(true);
    const [enabled, setEnabled] = useState(true);
    const [services, setServices] = useState<Service[]>([]);

    useEffect(() => {
        fetchServices();
        // Refetch when returning to the page (e.g. after editor save).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const fetchServices = async () => {
        try {
            setLoading(true);
            const response = await get('/services/');
            setEnabled(Boolean(response.data.enabled));
            setServices(response.data.data || []);
        } catch (error) {
            console.error('Error fetching services:', error);
        }
        setLoading(false);
    };

    const setTab = (tab: string) => {
        const params = new URLSearchParams(searchParams);
        if (tab === 'services') params.delete('tab');
        else params.set('tab', tab);
        params.delete('page');
        params.delete('per_page');
        setSearchParams(params);
    };

    const columns = useMemo(
        () => [
            {
                accessorKey: 'name',
                size: 240,
                header: 'Name',
                cell: ({ row }: any) => (
                    <span className="block truncate">
                        <span className="text-md font-semibold block truncate" title={row.original.name}>
                            {row.original.name}
                        </span>
                        <span className="text-xs font-mono text-muted-foreground block truncate" title={row.original.key}>
                            {row.original.key}
                        </span>
                    </span>
                ),
            },
            {
                id: 'publication',
                size: 190,
                header: 'Version',
                cell: ({ row }: any) => (
                    <span className="inline-flex items-center gap-2">
                        <ServiceStatusBadge status={publicationLabel(row.original)} />
                        <span className="text-sm text-muted-foreground">{describePublication(row.original)}</span>
                    </span>
                ),
            },
            {
                accessorKey: 'status',
                size: 100,
                header: 'Status',
                cell: ({ getValue }: any) => <ServiceStatusBadge status={getValue()} />,
            },
            {
                id: 'acquisition',
                size: 160,
                header: 'Customers get it',
                cell: ({ row }: any) => (
                    <span className="text-sm block truncate">
                        {ACQUISITION_OPTIONS.find((option) => option.value === row.original.acquisition)?.label || row.original.acquisition}
                        <span className="text-xs text-muted-foreground block">{row.original.visible ? 'Visible' : 'Hidden'}</span>
                    </span>
                ),
            },
            {
                accessorKey: 'completion',
                size: 100,
                header: 'Completion',
                cell: ({ getValue }: any) => (
                    <span className="text-sm text-muted-foreground">{getValue() === 'ai' ? 'Bounded' : getValue() === 'standing' ? 'Standing' : '—'}</span>
                ),
            },
            {
                id: 'jobs',
                size: 120,
                header: 'Jobs',
                cell: ({ row }: any) => (
                    <span className="text-md tabular-nums">
                        {row.original.jobs_on ?? 0} on
                        {row.original.jobs_blocked ? (
                            <span className="text-xs text-amber-700 dark:text-amber-400 block">{row.original.jobs_blocked} waiting</span>
                        ) : null}
                    </span>
                ),
            },
            {
                accessorKey: 'last_wake_at',
                size: 120,
                header: 'Last wake',
                cell: ({ getValue }: any) => <span className="text-md text-muted-foreground">{formatRelativeTime(getValue())}</span>,
            },
        ],
        [],
    );

    const table = useReactTable({ data: services, columns, getCoreRowModel: getCoreRowModel() });

    return (
        <div className="p-12 overflow-y-auto max-h-[calc(100vh-1px)] scrollbar-thin grow">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-bold flex items-center gap-2">Services</h1>
                    <p className="mt-4 text-sm text-slate-500">
                        Jobs the AI owns for each of your customers and keeps working. Author the job once, publish it, and assign it — or let
                        customers subscribe.
                    </p>
                </div>
                <Button onClick={() => navigate('/services/new')}>
                    <Plus className="h-4 w-4 mr-1" /> New Service
                </Button>
            </div>
            {!loading && !enabled && (
                <div className="mt-4 flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
                    <TriangleAlert className="h-4 w-4 shrink-0" />
                    Services are not enabled for this organization yet — you can author and publish, but jobs won't wake.
                </div>
            )}
            <Separator className="my-4" />

            <Tabs value={activeTab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="services">Services</TabsTrigger>
                    <TabsTrigger value="jobs">Jobs</TabsTrigger>
                    <TabsTrigger value="needs-you">Needs you</TabsTrigger>
                    <TabsTrigger value="events">Events</TabsTrigger>
                    <TabsTrigger value="sdk">SDK</TabsTrigger>
                </TabsList>

                <TabsContent value="services" className="mt-4">
                    <div className="border rounded-lg shadow overflow-hidden">
                        <Table className="table-fixed w-full">
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <TableHead key={header.id} style={{ width: header.getSize() }} className="truncate">
                                                {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            {loading ? (
                                <TableBody>
                                    {[1, 2].map((i) => (
                                        <TableRow key={i} className="animate-pulse">
                                            {columns.map((_, j) => (
                                                <TableCell key={j} className="px-6 py-4">
                                                    <div className="h-4 bg-muted rounded w-3/4" />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            ) : (
                                <TableBody>
                                    {table.getRowModel().rows.length ? (
                                        table.getRowModel().rows.map((row) => (
                                            <TableRow key={row.id} className="text-md cursor-pointer" onClick={() => navigate(`/services/${row.original.id}`)}>
                                                {row.getVisibleCells().map((cell) => (
                                                    <TableCell key={cell.id} style={{ width: cell.column.getSize() }} className="overflow-hidden">
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={columns.length} className="h-24 text-center">
                                                No services yet. Create one to give your customers a job the AI owns.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            )}
                        </Table>
                    </div>
                </TabsContent>

                <TabsContent value="jobs" className="mt-4">
                    <JobsList active={activeTab === 'jobs'} services={services} />
                </TabsContent>

                <TabsContent value="needs-you" className="mt-4">
                    <NeedsYou active={activeTab === 'needs-you'} />
                </TabsContent>

                <TabsContent value="events" className="mt-4">
                    <EventsTab active={activeTab === 'events'} />
                </TabsContent>

                <TabsContent value="sdk" className="mt-4">
                    <ServiceSdkKeysTab active={activeTab === 'sdk'} />
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default ServicesPage;
